import { Box, InputAdornment, MenuItem, TextField, TextFieldProps } from '@mui/material';
import { Controller, useFormContext, FieldError } from 'react-hook-form';
import CustomLabel from 'components/common/CustomLabel/CustomLabel';
import { colors } from 'styles/colors';
import { fonts } from 'styles/fonts';
import useGlobalStyles from 'styles/globalStyles';
import { ReactNode } from 'react';

type SelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

type CustomFormSelectProps = {
  name: string;
  error?: FieldError;
  placeholder?: string;
  options: SelectOption[];
  label?: string;
  required?: boolean;
  note?: string;
  floatingLabel?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
} & Omit<TextFieldProps, 'name' | 'error' | 'helperText' | 'select'>;

export default function CustomFormSelect({
  name,
  error,
  placeholder,
  options,
  label,
  required,
  note,
  sx,
  floatingLabel,
  startIcon,
  endIcon,
  ...rest
}: CustomFormSelectProps) {
  const globalStyles = useGlobalStyles();
  const { control } = useFormContext();

  return (
    <Box>
      {label && (
        <CustomLabel
          label={label}
          note={note}
          required={required}
        />
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            size="small"
            fullWidth
            value={field.value ?? ''}
            error={!!error}
            helperText={error?.message}
            label={floatingLabel}
            slotProps={{
              input: {
                notched: true,
                startAdornment: startIcon ? (
                  <InputAdornment position="end">
                    {startIcon}
                  </InputAdornment>
                ) : undefined,
                endAdornment: endIcon ? (
                  <InputAdornment position="end">
                    {endIcon}
                  </InputAdornment>
                ) : undefined,
                sx: {
                  fontSize: '15px',
                  fontFamily: fonts.neue,
                  fontWeight: 500,
                  color: colors.black,
                }
              },
              inputLabel: {
                shrink: true,
                required: required,
                className: globalStyles.floatingLabelText,
              },
              formHelperText: {
                sx: { ml: 0 },
              },
              select: {
                displayEmpty: true,
                renderValue: (value) => {
                  if ((value === '' || value === undefined) && placeholder) {
                    return (
                      <span
                        className={globalStyles.textInputPlaceholder}
                      >
                        {placeholder}
                      </span>
                    );
                  }

                  if ((value === '' || value === undefined) && floatingLabel) {
                    return (
                      <span
                        className={globalStyles.textInputPlaceholder}
                      >
                        {floatingLabel}
                      </span>
                    );
                  }

                  const selected = options.find(
                    (option) => option.value === value
                  );

                  return selected?.label ?? '';
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.secondary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.secondary,
              },
              '& .MuiSelect-select': {
                fontSize: '15px',
                fontFamily: fonts.neue,
                fontWeight: 500,
                color: colors.black,
              },
              ...sx,
            }}
            {...rest}
          >
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                sx={{
                  fontSize: '14px',
                  fontFamily: fonts.neue,
                  fontWeight: 500,
                  color: colors.black,
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Box>
  );
}
